import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleAxiosError } from '../../axios/handleAxiosError';
import { handleError } from '../../utils/handleError';
import { ODataParams } from '../_ODataTypes';
import { buildApiUrl } from '../../axios/urlCreator';
import { cachedGet } from '../../axios/responseCache';
import { QtyDocumentDataRequest, QtyDocumentDataResponse, QtyResponse, ReportingInitials, SummaryQtyFilterRecord, SummaryResponseListResponse } from './_reportingTypes';

const initialState: ReportingInitials = {
    summaryList: null,
    summaryQty: null,
    summaryQtyDocumentData: null,
    loading: false,
    error: null,
};

const reportingSlice = createSlice({
    name: 'reporting',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // GET Summary List
            .addCase(getSummaryList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSummaryList.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.summaryList = action.payload;
            })
            .addCase(getSummaryList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // GET Summary Qty
            .addCase(getSummaryQty.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSummaryQty.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.summaryQty = action.payload;
            })
            .addCase(getSummaryQty.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // GET Summary Qty Document Data
            .addCase(getSummaryQtyDocumentData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSummaryQtyDocumentData.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.summaryQtyDocumentData = action.payload;
            })
            .addCase(getSummaryQtyDocumentData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

// GET Summary List
export const getSummaryList = createAsyncThunk<SummaryResponseListResponse, ODataParams | void>(
    'reporting/getSummaryList',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params?.filter) queryParams.append('$filter', params.filter);
            if (params?.orderby) queryParams.append('$orderby', params.orderby);
            if (params?.top != null) queryParams.append('$top', params.top.toString());
            if (params?.skip != null) queryParams.append('$skip', params.skip.toString());

            return await cachedGet<SummaryResponseListResponse>(buildApiUrl("reporting/summaries", queryParams), 30_000);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

// GET Summary Qty
export const getSummaryQty = createAsyncThunk<QtyResponse, SummaryQtyFilterRecord | void>(
    'reporting/getSummaryQty',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params?.slaId) queryParams.append('slaId', params.slaId);
            if (params?.channel) queryParams.append('channel', params.channel);
            if (params?.startDate) queryParams.append('startDate', params.startDate.toString());
            if (params?.endDate) queryParams.append('endDate', params.endDate.toString());

            return await cachedGet<QtyResponse>(buildApiUrl("reporting/summaryQty", queryParams), 30_000);
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

// GET Summary Qty Document Data
export const getSummaryQtyDocumentData = createAsyncThunk<QtyDocumentDataResponse[], QtyDocumentDataRequest>(
    'reporting/getSummaryQtyDocumentData',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('viewType', params.viewType);
            queryParams.append('date', params.date);

            return await cachedGet<QtyDocumentDataResponse[]>(buildApiUrl("reporting/summaryQtyDocumentData", queryParams), 30_000);
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

export default reportingSlice.reducer;
